/**
 * Audio Firestore Service
 * Handles CRUD operations for audio books collection
 */

import { database, storage } from '../config/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  serverTimestamp,
  increment,
  CollectionReference,
  DocumentData,
  QueryDocumentSnapshot,
  writeBatch
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import toast from 'react-hot-toast';
import { AudioBook } from '@brovicms/models';
import { 
  AudioFormData, 
  AudioQueryOptions, 
  ServiceResult, 
  ListResult, 
  AudioStats 
} from '../types/admin';

class AudioService {
  private db: typeof database;
  private collectionName: string;
  private collection: CollectionReference<DocumentData>;

  constructor() {
    this.db = database;
    this.collectionName = 'audiobooks';
    this.collection = collection(this.db, this.collectionName);
  }

  /**
   * Validate audio data
   * @param {Object} audioData - Audio data to validate
   * @param {boolean} isUpdate - Whether this is an update operation
   * @returns {Object} Validation result
   */
  validateAudioData(audioData: Partial<AudioFormData>, isUpdate = false): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required fields
    const requiredFields: (keyof AudioFormData)[] = ['title', 'author', 'description'];
    requiredFields.forEach(field => {
      const value = audioData[field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(`${field} is required`);
      }
    });

    // Title validation
    if (audioData.title && audioData.title.length < 3) {
      errors.push('Title must be at least 3 characters long');
    }

    if (audioData.title && audioData.title.length > 200) {
      errors.push('Title must be less than 200 characters');
    }

    // Author validation
    if (audioData.author && audioData.author.length < 2) {
      errors.push('Author name must be at least 2 characters long');
    }

    // Description validation
    if (audioData.description && audioData.description.length < 10) {
      errors.push('Description must be at least 10 characters long');
    }

    if (audioData.description && audioData.description.length > 2000) {
      errors.push('Description must be less than 2000 characters');
    }

    // Duration validation
    if (audioData.duration !== undefined && audioData.duration !== null) {
      if (typeof audioData.duration !== 'number' || audioData.duration < 0) {
        errors.push('Duration must be a positive number (in seconds)');
      }
    }

    // File size validation
    if (audioData.fileSize !== undefined && audioData.fileSize !== null) {
      if (typeof audioData.fileSize !== 'number' || audioData.fileSize < 0) {
        errors.push('File size must be a positive number (in bytes)');
      }
    }

    // Price validation
    if (audioData.price !== undefined && audioData.price !== null) {
      if (typeof audioData.price !== 'number' || audioData.price < 0) {
        errors.push('Price must be a positive number');
      }
    }

    // Language validation
    if (audioData.language && audioData.language.length < 2) {
      errors.push('Language must be at least 2 characters');
    }

    // Narrator validation
    if (audioData.narrator && audioData.narrator.length < 2) {
      errors.push('Narrator name must be at least 2 characters long');
    }

    // File validation (for new audio)
    if (!isUpdate) {
      if (!audioData.audioUrl && !audioData.audioFile && !audioData.file?.url) {
        errors.push('Audio file is required');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Format duration from seconds to readable format
   * @param {number} seconds - Duration in seconds
   * @returns {string} Formatted duration
   */
  formatDuration(seconds: number): string {
    if (!seconds || seconds < 0) return '0:00';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  }

  /**
   * Parse duration string to seconds
   * @param {string} durationString - Duration string (e.g., "1:30:45" or "5:30")
   * @returns {number} Duration in seconds
   */
  parseDuration(durationString: string): number {
    if (!durationString) return 0;
    
    const parts = durationString.split(':').map((part: string) => parseInt(part, 10));
    
    if (parts.length === 3) {
      // Format: HH:MM:SS
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      // Format: MM:SS
      return parts[0] * 60 + parts[1];
    } else if (parts.length === 1) {
      // Format: SS
      return parts[0];
    }
    
    return 0;
  }

  /**
   * Create a draft audio document
   * @returns Draft document ID
   */
  async createDraft(): Promise<ServiceResult<{ docId: string }>> {
    try {
      const draftData = {
        isDraft: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      };

      const docRef = await addDoc(this.collection, draftData);
      
      return {
        success: true,
        id: docRef.id,
        data: { docId: docRef.id }
      };
    } catch (error: any) {
      console.error('Create draft error:', error);
      toast.error(`Failed to create draft: ${error.message}`);
      throw error;
    }
  }

  /**
   * Upload file to Firebase Storage
   * @param docId - Document ID
   * @param file - File to upload
   * @param type - Type of file (cover, audio)
   * @returns Download URL
   */
  async uploadFile(docId: string, file: File, type: 'cover' | 'audio'): Promise<string> {
    try {
      let filePath: string;
      
      switch (type) {
        case 'cover':
          filePath = `audiobooks/${docId}/coverpage.png`;
          break;
        case 'audio':
          const extension = file.name.split('.').pop() || 'mp3';
          filePath = `audiobooks/${docId}/audio_file.${extension}`;
          break;
        default:
          throw new Error('Invalid file type');
      }

      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (error: any) {
      console.error('Upload file error:', error);
      toast.error(`Failed to upload file: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update draft document with file URLs
   * @param docId - Document ID
   * @param updates - Updates to apply
   * @returns Update result
   */
  async updateDraft(docId: string, updates: Record<string, any>): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id: docId
      };
    } catch (error: any) {
      console.error('Update draft error:', error);
      toast.error(`Failed to update draft: ${error.message}`);
      throw error;
    }
  }

  /**
   * Publish draft (convert to published audio book)
   * @param docId - Document ID
   * @param audioData - Complete audio data
   * @returns Published audio book
   */
  async publishDraft(docId: string, audioData: Partial<AudioBook>): Promise<ServiceResult<AudioBook>> {
    try {
      // Convert Audio data to AudioFormData for validation
      const formData = {
        title: audioData.title,
        author: audioData.author,
        description: audioData.description,
        category: audioData.category?.name || '',
        duration: audioData.file?.duration || 0,
        fileSize: audioData.file?.fileSize,
        price: 0,
        isFree: true,
        file: audioData.file
      };
      
      // Validate data
      const validation = this.validateAudioData(formData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const docRef = doc(this.db, this.collectionName, docId);
      
      // Prepare published data
      const publishedData = {
        ...audioData,
        updatedBy: 'admin',
        isDraft: false,
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'published',
        isPublished: true,
      };

      await updateDoc(docRef, publishedData);

      const result: ServiceResult<AudioBook> = {
        success: true,
        id: docId,
        data: { ...audioData, id: docId } as AudioBook
      };

      toast.success('Audio book published successfully');
      return result;

    } catch (error: any) {
      console.error('Publish draft error:', error);
      toast.error(`Failed to publish audio book: ${error.message}`);
      throw error;
    }
  }

  /**
   * Delete draft document
   * @param docId - Document ID
   * @returns Deletion result
   */
  async deleteDraftDocOnly(docId: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, docId);
      await deleteDoc(docRef);

      return {
        success: true,
        id: docId
      };
    } catch (error: any) {
      console.error('Delete draft error:', error);
      throw error;
    }
  }

  /**
   * Delete file from Firebase Storage
   * @param fileUrl - URL of the file to delete
   * @returns Deletion result
   */
  async deleteFileFromStorage(fileUrl: string): Promise<ServiceResult<void>> {
    try {
      if (!fileUrl) {
        throw new Error('File URL is required');
      }

      // Extract file path from URL
      const url = new URL(fileUrl);
      const pathMatch = url.pathname.match(/\/o\/(.+)\?/);
      
      if (!pathMatch) {
        throw new Error('Invalid file URL format');
      }

      const filePath = decodeURIComponent(pathMatch[1]);
      const fileRef = ref(storage, filePath);
      
      await deleteObject(fileRef);

      return {
        success: true,
        id: filePath
      };
    } catch (error: any) {
      console.error('Delete file from storage error:', error);
      toast.error(`Failed to delete file: ${error.message}`);
      throw error;
    }
  }

  /**
   * Create a new audio book
   * @param {Object} audioData - Audio data
   * @returns {Promise<Object>} Created audio with ID
   */
  async createAudio(audioData: Partial<AudioFormData>): Promise<ServiceResult<AudioBook>> {
    try {
      // Validate data
      const validation = this.validateAudioData(audioData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Parse duration if provided as string
      let duration = audioData.duration;
      if (typeof audioData.duration === 'string') {
        duration = this.parseDuration(audioData.duration);
      }

      // Prepare document data
      const docData = {
        title: audioData.title?.trim() || '',
        author: audioData.author?.trim() || '',
        narrator: audioData.narrator || '',
        description: audioData.description?.trim() || '',
        category: audioData.category,
        subcategory: audioData.subcategory || '',
        tags: audioData.tags || [],
        price: audioData.price || 0,
        isFree: audioData.isFree || audioData.price === 0,
        duration: duration || 0,
        durationFormatted: this.formatDuration(duration || 0),
        fileSize: audioData.fileSize || 0,
        fileSizeFormatted: this.formatFileSize(audioData.fileSize || 0),
        language: audioData.language || 'English',
        isbn: audioData.isbn || '',
        publisher: audioData.publisher || '',
        publishedDate: audioData.publishedDate || null,
        audioUrl: audioData.audioUrl || '',
        coverImageUrl: audioData.coverImageUrl || '',
        status: audioData.status || 'draft', // draft, published, archived
        featured: audioData.featured || false,
        downloadCount: 0,
        playCount: 0,
        rating: 0,
        ratingCount: 0,
        quality: audioData.quality || 'standard', // standard, high, premium
        bitrate: audioData.bitrate || 128, // kbps
        sampleRate: audioData.sampleRate || 44100, // Hz
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: audioData.createdBy || 'admin',
        lastModifiedBy: audioData.createdBy || 'admin'
      };

      // Add document to Firestore
      const docRef = await addDoc(this.collection, docData);
      
      const result = {
        success: true,
        id: docRef.id,
        data: { ...docData, id: docRef.id }
      };

      toast.success('Audio book created successfully');
      return result as unknown as ServiceResult<AudioBook>;

    } catch (error) {
      console.error('Create audio error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to create audio book: ${errorMessage}`);
      throw error;
    }
  }

  /**
   * Get audio by ID
   * @param {string} id - Audio ID
   * @returns {Promise<Object>} Audio data
   */
  async getAudio(id: string): Promise<ServiceResult<AudioBook>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Audio book not found');
      }

      return {
        success: true,
        id: docSnap.id,
        data: { ...docSnap.data(), id: docSnap.id } as AudioBook
      };
    } catch (error: any) {
      console.error('Get audio error:', error);
      toast.error(`Failed to get audio book: ${error.message}`);
      throw error;
    }
  }

  /**
   * Update audio book
   * @param {string} id - Audio ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Update result
   */
  async updateAudio(id: string, updateData: Partial<AudioFormData>): Promise<ServiceResult<AudioBook>> {
    try {
      // Validate data
      const validation = this.validateAudioData(updateData, true);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const docRef = doc(this.db, this.collectionName, id);
      
      // Parse duration if provided as string
      let duration = updateData.duration;
      if (typeof updateData.duration === 'string') {
        duration = this.parseDuration(updateData.duration);
      }

      // Prepare update data
      const docData: any = {
        ...updateData,
        updatedAt: serverTimestamp(),
        lastModifiedBy: (updateData as any).lastModifiedBy || 'admin'
      };

      // Update formatted fields if duration or fileSize changed
      if (duration !== undefined) {
        docData.duration = duration;
        docData.durationFormatted = this.formatDuration(duration);
      }

      if (updateData.fileSize !== undefined) {
        docData.fileSizeFormatted = this.formatFileSize(updateData.fileSize);
      }

      // Clean up undefined values
      Object.keys(docData).forEach(key => {
        if (docData[key] === undefined) {
          delete docData[key];
        }
      });

      await updateDoc(docRef, docData);

      const result: ServiceResult<any> = {
        success: true,
        id,
        data: { ...docData, id }
      };

      toast.success('Audio book updated successfully');
      return result;

    } catch (error: any) {
      console.error('Update audio error:', error);
      toast.error(`Failed to update audio book: ${error.message}`);
      throw error;
    }
  }

  /**
   * Delete audio book
   * @param {string} id - Audio ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteAudio(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await deleteDoc(docRef);

      const result = {
        success: true,
        id,
        deletedAt: new Date().toISOString()
      };

      toast.success('Audio book deleted successfully');
      return result;

    } catch (error: any) {
      console.error('Delete audio error:', error);
      toast.error(`Failed to delete audio book: ${error.message}`);
      throw error;
    }
  }

  /**
   * Get all audio books with pagination and filtering
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Audio books list with pagination info
   */
  async getAudioBooks(options: AudioQueryOptions = {}): Promise<ListResult<AudioBook>> {
    try {
      const {
        limit: limitCount = 20,
        orderBy: orderByField = 'createdAt',
        orderDirection = 'desc',
        status = null,
        category = null,
        featured = null,
        search = null,
        isPublished = true,
        startAfterDoc = null
      } = options;

      let q = query(this.collection);

      // Apply filters
      if (status) {
        q = query(q, where('status', '==', status));
      }

      if (category) {
        q = query(q, where('category', '==', category));
      }

      if (featured !== null) {
        q = query(q, where('featured', '==', featured));
      }

      // Filter by published flag if explicitly provided
      if (typeof isPublished === 'boolean') {
        q = query(q, where('isPublished', '==', isPublished));
      }

      // Quality filter removed as it's not in AudioQueryOptions interface

      // Apply ordering
      q = query(q, orderBy(orderByField, orderDirection));

      // Apply pagination
      // q = query(q, limit(limitCount));

      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }

      const querySnapshot = await getDocs(q);
      const audioBooks: AudioBook[] = [];
      let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;

      querySnapshot.forEach((doc) => {
        audioBooks.push({
          id: doc.id,
          ...doc.data()
        } as AudioBook);
        lastDoc = doc;
      });

      return {
        success: true,
        data: audioBooks,
        error: null,
        pagination: {
          hasMore: audioBooks.length === limitCount,
          lastDoc,
          total: audioBooks.length
        }
      };

    } catch (error: any) {
      console.error('Get audio books error:', error);
      toast.error(`Failed to get audio books: ${error.message}`);
      throw error;
    }
  }

  /**
   * Search audio books
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchAudioBooks(searchTerm: string, options: { limit?: number } = {}): Promise<ListResult<AudioBook>> {
    try {
      const { limit: limitCount = 20 } = options;
      
      const q = query(
        this.collection,
        where('status', '==', 'published'),
        orderBy('title'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const allAudioBooks: AudioBook[] = [];
      
      querySnapshot.forEach((doc) => {
        allAudioBooks.push({
          id: doc.id,
          ...doc.data()
        } as AudioBook);
      });

      // Client-side filtering
      const filteredAudioBooks = allAudioBooks.filter(audio => {
        const searchLower = searchTerm.toLowerCase();
        return (
          audio.title.toLowerCase().includes(searchLower) ||
          audio.author.toLowerCase().includes(searchLower) ||
          (audio.narrator || '').toLowerCase().includes(searchLower) ||
          audio.description.toLowerCase().includes(searchLower) ||
          audio.tags.some((tag: string) => tag.toLowerCase().includes(searchLower))
        );
      });

      return {
        success: true,
        data: filteredAudioBooks,
        error: null,
        pagination: {
          hasMore: false,
          lastDoc: null,
          total: filteredAudioBooks.length
        }
      };

    } catch (error: any) {
      console.error('Search audio books error:', error);
      toast.error(`Search failed: ${error.message}`);
      throw error;
    }
  }

  /**
   * Increment download count
   * @param {string} id - Audio ID
   * @returns {Promise<Object>} Update result
   */
  async incrementDownloadCount(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await updateDoc(docRef, {
        downloadCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id
      };
    } catch (error) {
      console.error('Increment download count error:', error);
      throw error;
    }
  }

  /**
   * Increment play count
   * @param {string} id - Audio ID
   * @returns {Promise<Object>} Update result
   */
  async incrementPlayCount(id: string): Promise<ServiceResult<void>> {
    try {
      const docRef = doc(this.db, this.collectionName, id);
      await updateDoc(docRef, {
        playCount: increment(1),
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id
      };
    } catch (error) {
      console.error('Increment play count error:', error);
      throw error;
    }
  }

  /**
   * Update audio rating
   * @param {string} id - Audio ID
   * @param {number} rating - New rating (1-5)
   * @returns {Promise<Object>} Update result
   */
  async updateRating(id: string, rating: number): Promise<ServiceResult<{ newRating: number; newCount: number }>> {
    try {
      if (rating < 1 || rating > 5) {
        throw new Error('Rating must be between 1 and 5');
      }

      const docRef = doc(this.db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Audio book not found');
      }

      const currentData = docSnap.data();
      const currentRating = currentData.rating || 0;
      const currentCount = currentData.ratingCount || 0;
      
      const newCount = currentCount + 1;
      const newRating = ((currentRating * currentCount) + rating) / newCount;

      await updateDoc(docRef, {
        rating: Math.round(newRating * 10) / 10,
        ratingCount: newCount,
        updatedAt: serverTimestamp()
      });

      return {
        success: true,
        id,
        data: {
          newRating,
          newCount
        }
      };
    } catch (error) {
      console.error('Update rating error:', error);
      throw error;
    }
  }

  /**
   * Format file size to human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get audio statistics
   * @returns {Promise<Object>} Statistics data
   */
  async getAudioStats(): Promise<ServiceResult<AudioStats>> {
    try {
      const q = query(this.collection);
      const querySnapshot = await getDocs(q);
      
      let totalAudio = 0;
      let publishedAudio = 0;
      let draftAudio = 0;
      let totalDownloads = 0;
      let totalPlays = 0;
      let totalDuration = 0;
      const categories: Record<string, number> = {};
      const qualities: Record<string, number> = {};

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        totalAudio++;
        
        if (data.status === 'published') publishedAudio++;
        if (data.status === 'draft') draftAudio++;
        
        totalDownloads += data.downloadCount || 0;
        totalPlays += data.playCount || 0;
        totalDuration += data.duration || 0;
        
        const category = data.category || 'Uncategorized';
        categories[category] = (categories[category] || 0) + 1;
        
        const quality = data.quality || 'standard';
        qualities[quality] = (qualities[quality] || 0) + 1;
      });

      return {
        success: true,
        data: {
          totalAudio,
          publishedAudio,
          draftAudio,
          totalPlays,
          totalDuration,
          totalDurationFormatted: this.formatDuration(totalDuration),
          categories,
          qualities
        } as AudioStats
      };
    } catch (error) {
      console.error('Get audio stats error:', error);
      throw error;
    }
  }

  //write a function to delete draft audio books
  async deleteDraftAudioBooks(): Promise<ServiceResult<void>> {
    try {
      const q = query(this.collection, where('isDraft', '==', true));
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(this.db);
      querySnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      return {
        success: true,
        id: 'draftAudioBooks'
      } as ServiceResult<void>;
    }
    catch (error: any) {
      console.error('Delete draft audio books error:', error);
      toast.error(`Failed to delete draft audio books: ${error.message}`);
      throw error;
    }
  }
}

// Create and export singleton instance
const audioService = new AudioService();
export default audioService;
